<!-- src/apps/secret/components/receipt/StatusBadge.vue -->

<script setup lang="ts">
  import { type Receipt } from '@/schemas/shapes/v3/receipt';
  import OIcon from '@/shared/components/icons/OIcon.vue';
  import { useSecretExpiration } from '@/shared/composables/useSecretExpiration';
  import { getDisplayStatus, type DisplayStatus, getStatusText } from '@/utils/status';
  import { computed, watchEffect } from 'vue';
  import { useI18n } from 'vue-i18n';

  interface Props {
    record: Receipt;
    expiresIn?: number;
  }

  const props = defineProps<Props>();
  const { t } = useI18n();

  const { expirationState } = useSecretExpiration(
    props.record.created,
    props.record.expiration_in_seconds ?? 0
  );

  const status = computed((): DisplayStatus => {
    // getDisplayStatus is the single display authority: it validates the raw
    // lifecycle state and tolerates legacy aliases ('viewed'/'received'), so we
    // pass the value straight through. Re-validating here against the strict v3
    // schema would throw on legacy receipts the display layer is built to
    // absorb (#3829).
    const stateValue = props.record.secret_state || props.record.state;

    // Map expiration states to display states
    if (expirationState.value === 'expired') {
      return 'expired';
    }
    if (expirationState.value === 'warning') {
      return 'expiring_soon';
    }

    const displayStatus = getDisplayStatus(stateValue, props.expiresIn);
    if (displayStatus === 'new' && props.record.is_previewed) {
      return 'previewed';
    }
    return displayStatus;
  });

  // Terminal-pill tone maps: a hairline border + mono uppercase label + small
  // status dot, matching the receipt chrome (status strip, timeline). Each
  // display state carries a discreet accent instead of the former gradient
  // chips. previewed/revealed are canonical; viewed/received are deprecated
  // aliases mapped to the same tone.
  const statusClasses: Record<DisplayStatus, string> = {
    new: 'border-emerald-500/30 text-emerald-600 dark:text-emerald-400',
    unread: 'border-day-line text-day-ink-muted dark:border-white/[0.13] dark:text-night-ink-muted',
    viewed: 'border-blue-500/30 text-blue-600 dark:text-blue-400',
    previewed: 'border-blue-500/30 text-blue-600 dark:text-blue-400',
    burned: 'border-amber-500/30 text-amber-600 dark:text-amber-400',
    received: 'border-emerald-500/30 text-emerald-600 dark:text-emerald-400',
    revealed: 'border-emerald-500/30 text-emerald-600 dark:text-emerald-400',
    expiring_soon: 'border-orange-500/30 text-orange-600 dark:text-orange-400',
    orphaned: 'border-day-line text-day-ink-faint dark:border-white/[0.13] dark:text-night-ink-faint',
    expired: 'border-day-line text-day-ink-faint dark:border-white/[0.13] dark:text-night-ink-faint',
  };

  // Small status dot colour per display state.
  const statusDot: Record<DisplayStatus, string> = {
    new: 'bg-emerald-500 shadow-[0_0_6px_var(--color-emerald-500)]',
    unread: 'bg-day-ink-faint dark:bg-night-ink-faint',
    viewed: 'bg-blue-500',
    previewed: 'bg-blue-500',
    burned: 'bg-amber-500',
    received: 'bg-emerald-500',
    revealed: 'bg-emerald-500',
    expiring_soon: 'bg-orange-500',
    orphaned: 'bg-day-ink-faint dark:bg-night-ink-faint',
    expired: 'bg-day-ink-faint dark:bg-night-ink-faint',
  };

  const statusIcon: Record<DisplayStatus, string> = {
    new: 'check-circle-outline',
    unread: 'mark-email-unread-outline',
    viewed: 'mark-email-unread-outline',
    previewed: 'mark-email-unread-outline',
    burned: 'local-fire-department-rounded',
    received: 'mark-email-read-outline',
    revealed: 'mark-email-read-outline',
    expiring_soon: 'timer-outline',
    orphaned: 'warning-outline',
    expired: 'timer-off-outline',
  };

  const displayStatus = computed(() => getStatusText(status.value, t));

  // Handle invalid states
  watchEffect(() => {
    if (!status.value) {
      console.error(
        `Invalid receipt state: ${props.record?.state} || ${props.record?.secret_state}`
      );
    }
  });
</script>

<template>
  <span
    class="inline-flex items-center gap-1.5 border px-2.5 py-1 font-mono text-[10px] tracking-[0.08em] uppercase transition-colors duration-200"
    :class="[
      statusClasses[status],
      { 'animate-pulse': status === 'expiring_soon', 'opacity-75': status === 'expired' }
    ]"
    :title="displayStatus.description"
    role="status">
    <span
      class="inline-block size-[5px] shrink-0 rounded-full"
      :class="statusDot[status]"
      aria-hidden="true"></span>
    <OIcon
      collection="material-symbols"
      :name="statusIcon[status]"
      class="size-3.5" />
    {{ displayStatus.text }}
  </span>
</template>

<style scoped>
/* Ensure high contrast in dark mode with enhanced styling */
.animate-pulse {
  animation: statusPulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes statusPulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}
</style>
